<script setup lang="ts">
import { faCalendar, faClockRotateLeft, faTrashCan, faXmark } from '@fortawesome/pro-regular-svg-icons'

import { ModalConfirmation } from '@shared/ui/modals'
import { AppDropdown, AppDropdownItem } from '@shared/ui/dropdowns'
import { AppTooltip } from '@shared/ui/tooltips'
import { BtnIcon } from '@shared/ui/buttons'

import { LessonsActionModal } from '../../model/lessons/enums'
import { useLessonEditActions } from '../../model/lessons/lesson-edit-actions.hook'
import { useLessonEditDelete } from '../../model/lessons/lesson-edit-delete.hook'
import ModalLessonCancel from './modals/ModalLessonCancel.vue'
import ModalLessonReschedule from './modals/ModalLessonReschedule.vue'

defineOptions({
  name: 'LessonEditActions',
})

const {
  isOpen,
  containerRef,
  floatingRef,
  floatingStyles,
  triggerIcon,
  refLesson,
  refSlots,
  refActionModal,
  refCanManage,
  refCanViewChangelog,
  refCanDelete,
  refIsChangeDisabled,
  refChangeHint,
  refIsDeleteDisabled,
  deleteHint,
  refIsShown,
  toggle,
  closeMenu,
  openReschedule,
  openCancel,
  openChangelog,
  closeActionModal,
  afterActionSave,
} = useLessonEditActions()

const {
  refIsConfirmOpen: refIsDeleteConfirmOpen,
  refIsDeleting,
  refDetail: refDeleteDetail,
  openDelete,
  closeDelete,
  confirmDelete,
} = useLessonEditDelete()

const onDelete = (): void => {
  closeMenu()
  openDelete()
}
</script>

<template>
  <div
    v-if="refIsShown"
    ref="containerRef"
    class="lesson-edit-actions"
  >
    <BtnIcon
      :icon="triggerIcon"
      title="Lesson actions"
      @click="toggle"
    />
    <div
      v-if="isOpen"
      ref="floatingRef"
      class="lesson-edit-actions__floating"
      :style="floatingStyles"
    >
      <AppDropdown>
        <AppTooltip
          v-if="refCanManage"
          :text="refChangeHint"
          mode="disabled"
        >
          <AppDropdownItem
            text="Reschedule lesson"
            color="blue"
            :prepend-icon="faCalendar"
            :disabled="refIsChangeDisabled"
            @click="openReschedule"
          />
        </AppTooltip>
        <AppDropdownItem
          v-if="refCanViewChangelog"
          text="Show lesson changelog"
          color="blue"
          :prepend-icon="faClockRotateLeft"
          @click="openChangelog"
        />
        <AppTooltip
          v-if="refCanManage"
          :text="refChangeHint"
          mode="disabled"
        >
          <AppDropdownItem
            text="Cancel lesson"
            color="red"
            :prepend-icon="faXmark"
            :disabled="refIsChangeDisabled"
            @click="openCancel"
          />
        </AppTooltip>
        <AppTooltip
          v-if="refCanDelete"
          :text="deleteHint"
          mode="disabled"
        >
          <AppDropdownItem
            text="Delete lesson"
            color="red"
            :prepend-icon="faTrashCan"
            :disabled="refIsDeleteDisabled"
            @click="onDelete"
          />
        </AppTooltip>
      </AppDropdown>
    </div>
    <ModalLessonCancel
      v-if="refActionModal === LessonsActionModal.Cancel && refLesson"
      :lesson="refLesson"
      :after-save="afterActionSave"
      @close="closeActionModal"
    />
    <ModalLessonReschedule
      v-if="refActionModal === LessonsActionModal.Reschedule && refLesson"
      :lesson="refLesson"
      :slots="refSlots"
      :after-save="afterActionSave"
      @close="closeActionModal"
    />
    <ModalConfirmation
      :show="refIsDeleteConfirmOpen"
      :loading="refIsDeleting"
      :disabled="refIsDeleting"
      mode="delete"
      title="Delete lesson"
      :detail="refDeleteDetail"
      button-title="Delete lesson"
      cancel-title="Cancel"
      @click:confirm="confirmDelete"
      @click:cancel="closeDelete"
    />
  </div>
</template>

<style scoped lang="scss" src="./LessonEditActions.scss" />
